import React, { useState, useEffect, useRef, useCallback, useMemo, Ref, useImperativeHandle } from 'react';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getDayIndex, isValidDayOfWeek } from '@/core/utils/daysOfWeek';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime, Info } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import type { OperationMode } from '@/services';
import { OperationModesScheduleGroup, OperationModesSchedulesEditorProps } from '@features/shopautomation/types.ts';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { ConfirmationDialogWithIcon, WcnButton } from '@/components';
import { StatusTag } from '@/components/shared/Tags.tsx';
import { useSettingsStore } from '@core/store';
import { formatTime } from './scheduleDisplay';
import ScheduleTriggerFields from './ScheduleTriggerFields';
import ScheduleRows from './ScheduleRows';
import { useModeTagState } from '@core/hooks/useModeTagState.ts';
import FormTextField from '@/components/form/FormTextField.tsx';
import { required } from '@core/utils/validation.ts';
import { DAYS_OF_WEEK_FROM_MONDAY } from '@core/constants/daysOfWeek.ts';

export const TRIGGER_TYPES = { DAY: 'day', DATE: 'date' } as const;
export type TriggerType = (typeof TRIGGER_TYPES)[keyof typeof TRIGGER_TYPES];

export const LABEL_SX = {
    minWidth: 140,
    flexShrink: 0,
    pt: '2px',
    fontSize: '14px',
    fontWeight: 400,
    textTransform: 'uppercase',
    color: '#000'
};

export interface OperationModesSchedulesEditorRef {
    isDirty: boolean;
    save: () => Promise<boolean>;
}
interface Props extends OperationModesSchedulesEditorProps {
    isExpired?: boolean;
    isUpdate?: boolean;
    ref?: Ref<OperationModesSchedulesEditorRef>;
}

const OperationModesSchedulesEditor: React.FC<Props> = ({
    value,
    unavailableDaysDates,
    operationModes,
    activeOperationModes,
    onSave,
    onClose,
    isExpired = false,
    isUpdate = false,
    ref
}) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useSettingsStore();
    const formRef = useRef<FormContainerRef>(null);

    const getDefaultSchedule = useCallback(
        (val: OperationModesScheduleGroup | null): OperationModesScheduleGroup =>
            val && Object.keys(val).length > 0
                ? structuredClone(val)
                : {
                      placeholderId: uuidv4(),
                      name: '',
                      activeOn: null,
                      operationModeSchedules: [{ from: '00:00', operationMode: null }]
                  },
        []
    );

    const defaultSchedule = useMemo(() => getDefaultSchedule(value), [value, getDefaultSchedule]);
    const [schedule, setSchedule] = useState<OperationModesScheduleGroup>(() => defaultSchedule);
    const [trigger, setTrigger] = useState<TriggerType>(TRIGGER_TYPES.DAY);
    const [formValid, setFormValid] = useState(false);
    const confirmationDialogRef = useRef<any>(null);

    const { notifyWarning } = useNotifications();
    const getModeTagState = useModeTagState();

    const isValidDateTime = useCallback((val: string | null): boolean => !!val && DateTime.fromISO(val).isValid, []);

    const parseTime = useCallback((time: string): string => {
        const dt = DateTime.fromISO(time);
        return dt.isValid ? dt.toFormat('HH:mm') : time;
    }, []);

    const parseDate = useCallback(
        (date: string | null): string => {
            if (!date || !isValidDateTime(date)) return '';
            return DateTime.fromISO(date, { zone: 'UTC' }).toISODate() || '';
        },
        [isValidDateTime]
    );

    const isDateList = useCallback(
        (activeOn: string | null | undefined): boolean => !!activeOn && isValidDateTime(activeOn.split(',')[0]),
        [isValidDateTime]
    );

    const normalizeDateList = useCallback(
        (activeOn: string): string | null => {
            const dates = activeOn.split(',').map(parseDate).filter(Boolean);
            return dates.length ? dates.join(',') : null;
        },
        [parseDate]
    );

    const getShortDay = useCallback(
        (id: number): string => Info.weekdays('short', { locale: i18n.language || 'en' })[id],
        [i18n.language]
    );

    const disabledDay = useCallback(
        (dayName: string): boolean => unavailableDaysDates.includes(dayName) && !value?.activeOn?.includes(dayName),
        [unavailableDaysDates, value?.activeOn]
    );

    const unusedDate = useCallback((dateName: string): boolean => !disabledDay(dateName), [disabledDay]);

    const unusedTime = useCallback(
        (time: string): boolean =>
            schedule.operationModeSchedules.filter((x) => parseTime(x.from).localeCompare(parseTime(time)) === 0)
                .length <= 1,
        [schedule.operationModeSchedules, parseTime]
    );

    const timeRulesForIndex = (index: number) => [
        () => (unusedTime(schedule.operationModeSchedules[index].from) ? '' : t('entryInUse')),
        (val: any) => {
            const minutes = Number.parseInt(parseTime(val).split(':')[1], 10);
            return minutes % 15 === 0 ? '' : t('requiredFifteenMinuteMultiple');
        }
    ];

    const isSorted = useCallback(
        (): boolean =>
            schedule.operationModeSchedules.every(
                (item, index, array) =>
                    index === array.length - 1 || (item?.from?.localeCompare(array[index + 1]?.from) || 0) < 0
            ),
        [schedule.operationModeSchedules]
    );

    const sortSchedules = useCallback(() => {
        const sorted = [...schedule.operationModeSchedules].sort((a, b) => a?.from?.localeCompare(b?.from) || 0);
        setSchedule((prev) => ({ ...prev, operationModeSchedules: sorted }));
    }, [schedule.operationModeSchedules]);

    const sortIfRequired = useCallback(() => {
        if (!isSorted()) sortSchedules();
    }, [isSorted, sortSchedules]);

    const activeOnDates = useMemo(() => {
        if (!schedule.activeOn) return [];
        return schedule.activeOn
            .split(',')
            .filter(Boolean)
            .filter((d) => isValidDateTime(d));
    }, [schedule.activeOn, isValidDateTime]);

    const overriddenDays = useMemo(() => {
        if (trigger !== TRIGGER_TYPES.DATE || !activeOnDates.length) return [];

        const takenDays = new Set(unavailableDaysDates.filter((d) => isValidDayOfWeek(d)));
        if (!takenDays.size) return [];

        const hits = activeOnDates
            .filter((d) => d >= DateTime.local().toISODate())
            .map((d) => DateTime.fromISO(d))
            .filter((dt) => dt.isValid)
            .map((dt) => DAYS_OF_WEEK_FROM_MONDAY[dt.weekday - 1])
            .filter((day) => takenDays.has(day));

        return [...new Set(hits)];
    }, [trigger, activeOnDates, unavailableDaysDates, value?.activeOn]);

    const isDirty = useMemo(() => {
        return JSON.stringify(schedule) !== JSON.stringify(defaultSchedule);
    }, [schedule, defaultSchedule]);

    const performSave = useCallback(async (): Promise<boolean> => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }

        const hasMidnight = schedule.operationModeSchedules.some(
            (oms) => oms.from === '00:00' || oms.from === '00:00:00'
        );
        if (!hasMidnight) {
            notifyWarning('errors.invalidOperationModeSchedule');
            return false;
        }

        if (overriddenDays.length > 0) {
            confirmationDialogRef?.current?.openDialog();
        } else {
            onSave(schedule);
        }
        return true;
    }, [schedule, formValid, trigger, activeOnDates, onSave, notifyWarning, t]);

    const handleSave = useCallback(() => {
        void performSave();
    }, [performSave]);

    useImperativeHandle(ref, () => ({ isDirty, save: performSave }), [isDirty, performSave]);

    const handleActiveOnDayChange = useCallback((dayValue: string) => {
        setSchedule((prev) => {
            const currentDays = prev.activeOn?.split(',') || [];
            const newDays = currentDays.includes(dayValue)
                ? currentDays.filter((d) => d !== dayValue)
                : [...currentDays, dayValue];
            newDays.sort((a, b) => {
                const ia = isValidDayOfWeek(a) ? getDayIndex(a) : -1;
                const ib = isValidDayOfWeek(b) ? getDayIndex(b) : -1;
                return ia - ib;
            });
            return { ...prev, activeOn: newDays.length ? newDays.join(',') : null };
        });
    }, []);

    const handleDatesChange = useCallback((dates: string[]) => {
        const sorted = [...dates].sort((a, b) => a.localeCompare(b));
        setSchedule((prev) => ({ ...prev, activeOn: sorted.length ? sorted.join(',') : null }));
    }, []);

    const handleRemoveDate = useCallback((dateToRemove: string) => {
        setSchedule((prev) => {
            const current = prev.activeOn ? prev.activeOn.split(',').filter(Boolean) : [];
            const updated = current.filter((d) => d !== dateToRemove);
            return { ...prev, activeOn: updated.length ? updated.join(',') : null };
        });
    }, []);

    const handleTriggerChange = useCallback(
        (newTrigger: TriggerType) => {
            const original = value?.activeOn;
            let activeOn: string | null = null;

            if (newTrigger === TRIGGER_TYPES.DATE) {
                activeOn = isDateList(original) ? normalizeDateList(original!) : null;
            } else {
                activeOn = original && original !== 'standard' && !isDateList(original) ? original : null;
            }

            setSchedule((prev) => ({ ...prev, activeOn }));
            setTrigger(newTrigger);
        },
        [value?.activeOn, isDateList, normalizeDateList]
    );

    const addOperationModeSchedule = useCallback(() => {
        sortIfRequired();
        setSchedule((prev) => {
            const lastFrom = prev.operationModeSchedules[prev.operationModeSchedules.length - 1]?.from || '00:00';
            const dt = DateTime.fromISO(lastFrom);
            const newTime = dt.isValid ? dt.plus({ minutes: 15 }).toFormat('HH:mm') : '00:00';
            return {
                ...prev,
                operationModeSchedules: [
                    ...prev.operationModeSchedules,
                    { from: newTime, operationMode: null, placeholderId: uuidv4() }
                ]
            };
        });
    }, [sortIfRequired]);

    const removeOperationModeSchedule = useCallback((index: number) => {
        setSchedule((prev) => {
            const updated = [...prev.operationModeSchedules];
            updated.splice(index, 1);
            return { ...prev, operationModeSchedules: updated };
        });
    }, []);

    const handleTimeChange = useCallback((index: number, newValue: DateTime | null) => {
        if (newValue?.isValid) {
            setSchedule((prev) => {
                const updated = [...prev.operationModeSchedules];
                updated[index] = { ...updated[index], from: newValue.toFormat('HH:mm') };
                return { ...prev, operationModeSchedules: updated };
            });
        }
    }, []);

    const handleOperationModeChange = useCallback((index: number, val: string) => {
        setSchedule((prev) => {
            const updated = [...prev.operationModeSchedules];
            updated[index] = { ...updated[index], operationMode: val };
            return { ...prev, operationModeSchedules: updated };
        });
    }, []);

    const handleNameChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setSchedule((prev) => ({ ...prev, name: e.target.value })),
        []
    );

    const activeOnDays = useMemo(() => schedule.activeOn?.split(',') || [], [schedule.activeOn]);

    const filteredOperationModes = useMemo(
        () => operationModes.filter((mode: OperationMode) => activeOperationModes.includes(mode.name)),
        [operationModes, activeOperationModes]
    );

    const sortedForPreview = useMemo(
        () => [...schedule.operationModeSchedules].sort((a, b) => (a?.from || '').localeCompare(b?.from || '')),
        [schedule.operationModeSchedules]
    );

    const showMidnightBlock = useMemo(
        () => !sortedForPreview.some((item) => formatTime(item.from) === '00:00'),
        [sortedForPreview]
    );

    const isStandard = schedule.activeOn === 'standard';

    const editorTitle = useMemo(() => {
        if (!isUpdate) return t('addOperationModeSchedule');
        if (isExpired) return t('editExpiredOperationModeSchedule');
        return t('editOperationModeSchedule');
    }, [isUpdate, isExpired, t]);

    const TRIGGER_OPTIONS = [
        { label: t('operationModeScheduleTriggerDay').toUpperCase(), value: TRIGGER_TYPES.DAY },
        { label: t('operationModeScheduleTriggerDate').toUpperCase(), value: TRIGGER_TYPES.DATE }
    ];

    useEffect(() => {
        setSchedule(defaultSchedule);
        setTrigger(isDateList(defaultSchedule.activeOn) ? TRIGGER_TYPES.DATE : TRIGGER_TYPES.DAY);
        formRef.current?.resetValidation();
    }, [defaultSchedule, isDateList]);

    useEffect(() => {
        sortIfRequired();
    }, [value]);

    const previewItems = (
        <>
            {showMidnightBlock && (
                <Box sx={{ flex: 0.15, minWidth: 0 }}>
                    <Box
                        sx={{
                            bgcolor: '#ebebeb',
                            borderRadius: '3px',
                            px: 1,
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
                            …
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.25 }}>
                        <Typography variant="caption" sx={{ fontSize: '10px', color: 'var(--gray-medium)' }}>
                            00:00
                        </Typography>
                    </Box>
                </Box>
            )}
            {sortedForPreview.map((oms, index) => {
                const isLast = index === sortedForPreview.length - 1;
                return (
                    <Box key={oms.placeholderId || oms.id || index} sx={{ flex: 1, minWidth: 0 }}>
                        <StatusTag
                            state={getModeTagState(oms.operationMode)}
                            label={oms.operationMode ?? '—'}
                            style={{
                                width: '100%',
                                borderRadius: '3px',
                                justifyContent: 'flex-start'
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                            {(index === 0 && !showMidnightBlock) || index > 0 ? (
                                <Typography variant="caption" sx={{ fontSize: '10px', color: 'var(--gray-medium)' }}>
                                    {formatTime(oms.from)}
                                </Typography>
                            ) : (
                                <span />
                            )}
                            {isLast && (
                                <Typography variant="caption" sx={{ fontSize: '10px', color: 'var(--gray-medium)' }}>
                                    24:00
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
            <FormContainer formRef={formRef} onChange={setFormValid} value={formValid}>
                <Box
                    className="operation-modes-schedules-card"
                    sx={{
                        border: '1px solid var(--sidebar-divider)',
                        borderRadius: '3px',
                        backgroundColor: 'background.paper',
                        mt: 3,
                        mb: isMobile ? 2 : 8
                    }}
                >
                    <Box sx={{ px: isMobile ? 2 : 3, py: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                letterSpacing: '0.05em',
                                fontFamily: 'var(--font-family-primary)',
                                textTransform: 'uppercase'
                            }}
                        >
                            {editorTitle}
                        </Typography>
                    </Box>

                    <Box sx={{ px: isMobile ? 2 : 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {!isStandard &&
                            (isMobile ? (
                                <FormTextField
                                    name="name"
                                    className="operation-mode-schedule-name-field"
                                    label={t('name')}
                                    value={schedule.name || ''}
                                    onChange={handleNameChange}
                                    inputProps={{ maxLength: 40 }}
                                    sx={{ width: '100%' }}
                                    rules={[required(t)]}
                                    required
                                />
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                    <Typography sx={{ ...LABEL_SX, visibility: 'hidden' }} aria-hidden="true">
                                        {t('operationModeScheduleTrigger')}
                                    </Typography>
                                    <FormTextField
                                        name="name"
                                        className="operation-mode-schedule-name-field"
                                        label={t('name')}
                                        value={schedule.name || ''}
                                        onChange={handleNameChange}
                                        inputProps={{ maxLength: 20 }}
                                        sx={{ width: '315px' }}
                                        rules={[required(t)]}
                                        required
                                    />
                                </Box>
                            ))}

                        {!isStandard && (
                            <ScheduleTriggerFields
                                isMobile={isMobile}
                                trigger={trigger}
                                triggerOptions={TRIGGER_OPTIONS}
                                onTriggerChange={handleTriggerChange}
                                activeOnDays={activeOnDays}
                                onDayToggle={handleActiveOnDayChange}
                                disabledDay={disabledDay}
                                getShortDay={getShortDay}
                                activeOnDates={activeOnDates}
                                onDatesChange={handleDatesChange}
                                onRemoveDate={handleRemoveDate}
                                unusedDate={unusedDate}
                            />
                        )}

                        {isMobile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography sx={LABEL_SX}>{t('preview')}</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>{previewItems}</Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                <Typography sx={{ ...LABEL_SX, pt: '2px' }}>{t('preview')}</Typography>
                                <Box sx={{ flex: 1, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                                    {previewItems}
                                </Box>
                            </Box>
                        )}

                        <ScheduleRows
                            isMobile={isMobile}
                            operationModeSchedules={schedule.operationModeSchedules}
                            filteredOperationModes={filteredOperationModes}
                            timeRulesForIndex={timeRulesForIndex}
                            onTimeChange={handleTimeChange}
                            onOperationModeChange={handleOperationModeChange}
                            onAdd={addOperationModeSchedule}
                            onRemove={removeOperationModeSchedule}
                        />
                    </Box>

                    {isMobile ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 3
                            }}
                        >
                            <WcnButton
                                variant="primary"
                                onClick={handleSave}
                                disabled={!formValid}
                                style={{ width: '80%' }}
                            >
                                {t('save')}
                            </WcnButton>
                            <WcnButton variant="red" onClick={onClose} style={{ width: '80%' }}>
                                {t('cancel')}
                            </WcnButton>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1.5,
                                px: 3,
                                py: 3
                            }}
                        >
                            <WcnButton variant="red" onClick={onClose}>
                                {t('cancel')}
                            </WcnButton>
                            <WcnButton variant="primary" onClick={handleSave} disabled={!formValid}>
                                {t('saveChanges')}
                            </WcnButton>
                        </Box>
                    )}
                </Box>
            </FormContainer>
            <ConfirmationDialogWithIcon
                dialogRef={confirmationDialogRef}
                heading={t('operationModeSchedules.dayOverrideHeading')}
                message={t('operationModeSchedules.dayOverrideMessage')}
                onConfirm={() => onSave(schedule)}
                confirmText={t('saveAndOverwrite')}
                cancelText={t('dismiss')}
                icon={<i className="bi bi-exclamation-triangle warning-icon" />}
            />
        </LocalizationProvider>
    );
};

export default OperationModesSchedulesEditor;
